import { forwardRef, useState } from "react";
import { Icon, type IconName } from "@/components/common/Icon";
import { Spinner } from "@/components/common/Spinner";

/** 正方形ボタンの既定の一辺 (px)。同じ行に並ぶ見出しの高さを揃えるのに使う。 */
export const ICON_BUTTON_DEFAULT_SIZE = 24;

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
	icon: IconName;
	/** Pixel size of the icon glyph. Default 16. */
	iconSize?: number;
	/** アイコンの線の太さ。既定は Icon と同じ 1.3。 */
	iconStroke?: number;
	/** Square button edge length in px. Default ICON_BUTTON_DEFAULT_SIZE. */
	size?: number;
	/** アイコンの通常色。既定は --fg-2。 */
	color?: string;
	/** ホバー時のアイコンの色。既定は通常色のまま。 */
	hoverColor?: string;
	/** Idle background. Default transparent. */
	background?: string;
	/** ホバー時の背景。既定は --overlay-hover。 */
	hoverBackground?: string;
	/** 押下時の背景。既定は --overlay-active。 */
	activeBackground?: string;
	/** 押した操作の完了待ち。押せなくし、アイコンをスピナーに替える。待つだけなので減光はしない。 */
	busy?: boolean;
}

/**
 * Square icon button with self-contained hover and pressed visual states.
 * Hover and active are tracked in component state so they work with inline
 * styles (the rest of the app styles inline as well).
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
	{
		icon,
		iconSize = 16,
		iconStroke,
		size = ICON_BUTTON_DEFAULT_SIZE,
		color = "var(--fg-2)",
		hoverColor = color,
		background = "transparent",
		hoverBackground = "var(--overlay-hover)",
		activeBackground = "var(--overlay-active)",
		busy = false,
		disabled: disabledProp,
		style,
		onPointerEnter,
		onPointerLeave,
		onPointerDown,
		onPointerUp,
		onPointerCancel,
		...rest
	},
	ref,
) {
	const [hovered, setHovered] = useState(false);
	const [pressed, setPressed] = useState(false);
	const disabled = disabledProp || busy;

	const currentBackground = disabled
		? background
		: pressed
			? activeBackground
			: hovered
				? hoverBackground
				: background;
	const currentColor = !disabled && hovered ? hoverColor : color;

	return (
		<button
			ref={ref}
			type="button"
			disabled={disabled}
			data-busy={busy || undefined}
			onPointerEnter={(e) => {
				setHovered(true);
				onPointerEnter?.(e);
			}}
			onPointerLeave={(e) => {
				setHovered(false);
				setPressed(false);
				onPointerLeave?.(e);
			}}
			onPointerDown={(e) => {
				setPressed(true);
				onPointerDown?.(e);
			}}
			onPointerUp={(e) => {
				setPressed(false);
				onPointerUp?.(e);
			}}
			onPointerCancel={(e) => {
				setPressed(false);
				onPointerCancel?.(e);
			}}
			style={{
				width: size,
				height: size,
				display: "grid",
				placeItems: "center",
				background: currentBackground,
				border: 0,
				borderRadius: "var(--radius-2)",
				cursor: disabled ? "default" : "pointer",
				color: currentColor,
				opacity: disabled && !busy ? 0.5 : 1,
				transition: "background 120ms ease",
				...style,
			}}
			{...rest}
		>
			{busy ? (
				<Spinner size={iconSize - 2} color={currentColor} />
			) : (
				<Icon name={icon} size={iconSize} color={currentColor} stroke={iconStroke} />
			)}
		</button>
	);
});
